import { useMemo } from "react";

import { useIntegrations } from "./useIntegrations";
import { toSlug } from "../utils/resolve-module-name";

interface UseAvailableModulesParams {
  tenantId?: string | null;
  clientId?: string | null;
}

/**
 * Derives the set of module slugs available for the current tenant/client context from a single
 * unfiltered integrations request with `includes: ["modules"]`.
 *
 * This drives the integrations-list module filter options. It fetches its own unfiltered integration
 * list rather than reusing the page's filtered results — otherwise selecting a module filter would
 * collapse the options to only the modules of the matching integrations.
 */
export function useAvailableModules({
  tenantId,
  clientId,
}: UseAvailableModulesParams) {
  const integrationsQuery = useIntegrations({
    tenantId,
    clientId,
    includes: ["modules"],
  });

  const moduleSlugs = useMemo(() => {
    const integrations = integrationsQuery.data ?? [];
    const slugs = new Set<string>();
    for (const integration of integrations) {
      for (const mod of integration.modules ?? []) {
        slugs.add(toSlug(mod.moduleName));
      }
    }
    return Array.from(slugs);
  }, [integrationsQuery.data]);

  return { moduleSlugs, isPending: integrationsQuery.isPending };
}
